import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useDateRange } from "@/context/DateRangeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { inr, startOfMonth, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import CSVUploadDialog from "@/components/CSVUploadDialog";
import ChannelBreakdown from "@/components/ChannelBreakdown";

const CHANNEL_LABELS = {
  shopify: "Shopify", amazon: "Amazon", flipkart: "Flipkart",
  offline: "Offline / B2B", other: "Other",
};

export default function Orders() {
  const { current } = useWorkspace();
  const { dateFrom, dateTo, setDateFrom, setDateTo } = useDateRange();
  const [orders, setOrders] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [channelFilter, setChannelFilter] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    if (!current) return;
    setLoading(true);
    try {
      const [o, a] = await Promise.all([
        api.get("/orders", { params: { workspace_id: current.id, date_from: dateFrom, date_to: dateTo, channel: channelFilter || undefined } }),
        api.get("/sku-aliases", { params: { workspace_id: current.id } }),
      ]);
      setOrders(o.data);
      setAliases(a.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [current, dateFrom, dateTo, channelFilter, refreshKey]);

  const onCommitted = () => setRefreshKey((k) => k + 1);

  const deleteAlias = async (a) => {
    if (!window.confirm(`Delete alias ${a.channel} · ${a.alias_sku_code}?`)) return;
    await api.delete(`/sku-aliases/${a.id}`);
    toast.success("Alias removed");
    setRefreshKey((k) => k + 1);
  };

  if (!current) return <div className="p-12 text-muted-foreground">No workspace selected.</div>;

  return (
    <div className="min-h-screen">
      <div className="border-b border-border p-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-muted-foreground">{current.brand_name}</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1" data-testid="orders-title">Orders &amp; Uploads</h1>
          <p className="text-sm text-muted-foreground mt-2">Import multi-channel sales · manage SKU aliases · view per-order line items</p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="overline">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-none mt-1 h-10 w-40 font-mono" data-testid="orders-date-from" />
          </div>
          <div>
            <Label className="overline">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-none mt-1 h-10 w-40 font-mono" data-testid="orders-date-to" />
          </div>
          <Button onClick={() => setUploadOpen(true)} className="rounded-none h-10" data-testid="upload-csv-btn">
            <Upload className="h-4 w-4 mr-2" /> Upload CSV
          </Button>
        </div>
      </div>

      <Tabs defaultValue="orders" className="w-full">
        <div className="border-b border-border px-8">
          <TabsList className="bg-transparent rounded-none h-auto p-0 gap-6">
            <TabsTrigger value="orders" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:font-semibold px-0 py-3 h-auto text-sm" data-testid="tab-orders">
              Orders
            </TabsTrigger>
            <TabsTrigger value="channels" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:font-semibold px-0 py-3 h-auto text-sm" data-testid="tab-channels">
              Channels
            </TabsTrigger>
            <TabsTrigger value="aliases" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:font-semibold px-0 py-3 h-auto text-sm" data-testid="tab-aliases">
              SKU Aliases
              {aliases.length > 0 && <span className="ml-2 font-mono text-[10px] text-muted-foreground">{aliases.length}</span>}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="orders" className="p-8">
          <div className="mb-4 flex items-center gap-2">
            <span className="overline text-muted-foreground">Filter</span>
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className="border border-border bg-background px-3 py-2 text-sm h-9 font-mono"
              data-testid="orders-channel-filter"
            >
              <option value="">All channels</option>
              {Object.entries(CHANNEL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <span className="text-xs text-muted-foreground">{orders.length} rows{orders.length === 500 ? " (capped, refine range)" : ""}</span>
          </div>

          <div className="border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="overline">Date</TableHead>
                  <TableHead className="overline">Channel</TableHead>
                  <TableHead className="overline">Order ID</TableHead>
                  <TableHead className="overline">SKU</TableHead>
                  <TableHead className="overline">Master SKU</TableHead>
                  <TableHead className="overline text-right">Qty</TableHead>
                  <TableHead className="overline text-right">Unit ₹</TableHead>
                  <TableHead className="overline text-right">Gross</TableHead>
                  <TableHead className="overline text-right">Discount</TableHead>
                  <TableHead className="overline text-right">Net</TableHead>
                  <TableHead className="overline text-right">COGS</TableHead>
                  <TableHead className="overline text-right">Mkt Fee</TableHead>
                  <TableHead className="overline text-right">PG</TableHead>
                  <TableHead className="overline text-right">Ad Alloc</TableHead>
                  <TableHead className="overline text-right">Net Profit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="orders-table">
                {loading && <TableRow><TableCell colSpan={14} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
                {!loading && orders.length === 0 && <TableRow><TableCell colSpan={14} className="text-center text-muted-foreground py-8">No orders. Upload a CSV to begin.</TableCell></TableRow>}
                {orders.map((o) => (
                  <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                    <TableCell className="font-mono text-xs">{o.order_date}</TableCell>
                    <TableCell><span className="uppercase font-mono text-[10px] px-1.5 py-0.5 border border-border">{o.channel}</span></TableCell>
                    <TableCell className="font-mono text-xs">{o.external_order_id}</TableCell>
                    <TableCell className="font-mono text-xs">{o.sku_code}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{o.master_sku_code || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{o.qty}</TableCell>
                    <TableCell className="text-right font-mono">{inr(o.unit_price, { decimals: 2 })}</TableCell>
                    <TableCell className="text-right font-mono">{inr(o.gross_amount, { decimals: 0 })}</TableCell>
                    <TableCell className="text-right font-mono text-[hsl(var(--negative))]">{inr(o.discount, { decimals: 0 })}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{inr(o.net_amount, { decimals: 0 })}</TableCell>
                    <TableCell className="text-right font-mono">{inr(o.line_cogs, { decimals: 0 })}</TableCell>
                    <TableCell className="text-right font-mono text-[hsl(var(--negative))]">{inr(o.marketplace_fee || 0, { decimals: 0 })}</TableCell>
                    <TableCell className="text-right font-mono">{inr(o.line_fee || 0, { decimals: 0 })}</TableCell>
                    <TableCell className="text-right font-mono">{inr(o.allocated_ad || 0, { decimals: 0 })}</TableCell>
                    <TableCell className={`text-right font-mono font-semibold ${(o.net_profit || 0) >= 0 ? "text-[hsl(var(--positive))]" : "text-[hsl(var(--negative))]"}`} data-testid={`order-netprofit-${o.id}`}>{inr(o.net_profit || 0, { decimals: 0 })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="channels" className="p-8">
          <ChannelBreakdown workspaceId={current.id} dateFrom={dateFrom} dateTo={dateTo} refreshKey={refreshKey} />
        </TabsContent>

        <TabsContent value="aliases" className="p-8">
          <div className="border border-border" data-testid="aliases-table">
            <div className="p-4 border-b border-border">
              <div className="overline text-muted-foreground">Channel SKU → Master SKU</div>
              <div className="font-display text-base font-semibold">SKU Alias Mappings</div>
              <div className="text-xs text-muted-foreground mt-1">Created automatically during CSV imports. Delete an alias to force re-mapping on the next upload.</div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="overline">Channel</TableHead>
                  <TableHead className="overline">Alias SKU</TableHead>
                  <TableHead className="overline">Master SKU</TableHead>
                  <TableHead className="overline">Product</TableHead>
                  <TableHead className="overline text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {aliases.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No aliases yet. They'll appear here after you map unrecognized SKUs during a CSV import.</TableCell></TableRow>}
                {aliases.map((a) => (
                  <TableRow key={a.id} data-testid={`alias-row-${a.id}`}>
                    <TableCell><span className="uppercase font-mono text-[10px] px-1.5 py-0.5 border border-border">{a.channel}</span></TableCell>
                    <TableCell className="font-mono text-sm">{a.alias_sku_code}</TableCell>
                    <TableCell className="font-mono text-sm">{a.master_sku_code}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{a.master_product_name}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => deleteAlias(a)} data-testid={`alias-del-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <CSVUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onCommitted={onCommitted} />
    </div>
  );
}
